/* eslint-disable max-len */
/* eslint-disable no-unused-vars */
import React, {
    useState, useEffect, useCallback, useContext, useMemo,
    Fragment
} from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { TranslatorContext } from '@jutro/locale';
import { Redirect, withRouter } from 'react-router-dom';
import { ViewModelServiceContext, useDataRefresh } from 'gw-portals-viewmodel-react';
import { renderContentFromMetadata } from '@jutro/uiconfig';
import { useAuthentication } from 'gw-digital-auth-react';
import { ServiceRequestService } from 'gw-capability-servicerequest';
import {
    AcceptWorkActionPopup,
    CommonWorkActionPopup,
    AddQuoteModal,
    DelayWorkActionPopup,
    AddInvoicePopup,
    WithdrawInvoicePopup,
    ReplyMessagePopup,
    CreateNewMessagePopup
} from 'gw-capability-servicerequest-react';
import {
    Loader, ModalNextProvider, FormattedDate, Button, InputField
} from '@jutro/components';
import { Link, Currency as CurrencyField } from 'gw-components-platform-react';
import metadata from './ServiceRequestDetailsPage.metadata.json5';
import styles from './ServiceRequestDetailsPage.module.scss';
import messages from './ServiceRequestDetailsPage.messages';


function ServiceRequestDetailsPage(props) {
    const { match } = props;
    const { params } = match;
    const { serviceRequestNumber } = params;
    const { authHeader } = useAuthentication();
    const [isLoading, setLoadingState] = useState(false);
    const [isSalvage, setSalvage] = useState(false);
    const [serviceItems, setServiceItems] = useState({});
    const [serviceDetails, setServiceDetails] = useState({ otherOperations: [] });
    const translator = useContext(TranslatorContext);
    const viewModelService = useContext(ViewModelServiceContext);
    const [bidAcceptedStatus, setBidAcceptedStatus] = useState(false);
    const { refreshData } = useDataRefresh();

    const PRIMARY_PHONE_TYPE_MAP = {
        home: 'homeNumber',
        mobile: 'cellNumber',
        work: 'workNumber'
    };

    const readValue = useCallback((id, path) => {
        if (path === 'otherOperations') {
            return _.get(serviceDetails, path);
        }
        if (path === 'services') {
            const servicesList = _.get(serviceItems, path);
            if (servicesList && servicesList.length >= 1) {
                return servicesList.join(', ');
            }
            return _.get(serviceItems, path);
        }
        if (path === 'serviceAddress.displayName' || path === 'customerContact.primaryAddress.displayName') {
            const addressPath = path.slice(0, path.lastIndexOf('.'));
            const address = _.get(serviceItems, addressPath);
            if (address) {
                const addressFields = ['unitorComplexorBranch', 'addressLine1', 'city', 'suburb'];
                let addressValue = '';
                _.forEach(addressFields, (addr) => {
                    const addressData = _.get(address, addr);
                    if (addressData) {
                        if (addressValue.trim().length > 0) { addressValue += ', '; }
                        addressValue += addressData;
                    }
                });
                const postalCode = _.get(address, 'postalCode');
                if (postalCode) {
                    // eslint-disable-next-line prefer-template
                    addressValue += ' ' + postalCode;
                }
                return addressValue;
            }
        }
        return _.get(serviceItems, path);
    }, [serviceDetails, serviceItems]);

    const lookupActionNameMessageKey = useCallback(
        (action) => {
            const { isReviseQuote } = serviceItems;
            const { quoteExists } = serviceDetails;
            if (action) {
                if (action === 'addquote') {
                    if (isReviseQuote || quoteExists) {
                        return translator(messages.actionReviseQuote);
                    }
                    return translator(messages.actionAddQuote);
                }
                if (action === 'vendordelayed') { // not an operation in CC, but an available action
                    return translator(messages.actionReportADelay);
                }
                if (action === 'specialistacceptedwork') {
                    const typeOfService = _.get(serviceItems, 'requestKind');
                    if (typeOfService !== 'serviceonly') {
                        return translator(messages.specialistagreetoquote);
                    }
                    translator(messages.specialistacceptedwork);
                }
                if (action === 'addinvoice') {
                    const nextAction = _.get(serviceItems, 'nextStep');
                    if (nextAction !== 'Finish the work') {
                        return translator(messages.addinvoice);
                    }
                }
                if ((action === 'specialistdeclined' || action === 'salVendorDeclined_EXT') && isSalvage) {
                    return translator(messages.salvagevendordeclinetobid);
                }
                if (action === 'salVendorAgreedToBid_EXT' && isSalvage) {
                    return translator(messages.actionAgreeToBid);
                }
                if (action === 'salAddBid_EXT' && isSalvage) {
                    return translator(messages.actionAddBid);
                }
                if (action === 'specialistcanceled') {
                    const canCancelSalvage = _.get(serviceItems, 'canCancel_EXT');
                    if (isSalvage && canCancelSalvage) {
                        return translator(messages.actionTitleCancelBid);
                    }
                }
                return translator(messages[action]);
            }
            return null;
        }, [isSalvage, serviceDetails, serviceItems, translator]
    );

    const getServiceRequestDetails = useCallback(
        () => {
            setLoadingState(true);
            // ServiceRequestService.getServiceRequestDetails(serviceRequestNumber, authHeader)
            ServiceRequestService.getServiceRequestDetails_EXT(serviceRequestNumber, authHeader)
                .then((response) => {
                    const serviceData = {};
                    setServiceItems(response);
                    setBidAcceptedStatus(response.isBidAccepted_EXT);
                    serviceData.isOverdue = response.isOverdue;
                    serviceData.quoteExists = !_.isUndefined(response.latestQuote);
                    const hasDefaultOperation = !_.isUndefined(response.defaultOperation);
                    serviceData.firstOperation = hasDefaultOperation
                        ? response.defaultOperation
                        : _.head(response.otherOperations);
                    serviceData.otherOperations = hasDefaultOperation
                        ? response.otherOperations
                        : _.tail(response.otherOperations);
                    serviceData.dueDate = response.serviceDateApplies
                        ? response.expectedServiceCompletionDate
                        : response.expectedQuoteCompletionDate;

                    const customerContactPreferredNumberType = _.get(PRIMARY_PHONE_TYPE_MAP,
                        response.customerContact.primaryPhoneType);
                    const claimHandlerPreferredNumberType = _.get(PRIMARY_PHONE_TYPE_MAP,
                        response.claimHandler.primaryPhoneType);
                    serviceData.customerContactPreferredNumber = _.get(response.customerContact,
                        customerContactPreferredNumberType);
                    serviceData.claimHandlerPreferredNumber = _.get(response.claimHandler,
                        claimHandlerPreferredNumberType);

                    serviceData.invoiceTotal = {
                        amount: response.outstandingInvoiceAmount.amount
                            + response.paymentsMadeAmount.amount,
                        currency: response.outstandingInvoiceAmount.currency
                    };
                    serviceData.sortedHistory = _.sortBy(response.history, 'sequence');
                    if (response.additionalInstructions
                        && response.additionalInstructions.length > 100) {
                        serviceData.showFullInstructions = false;
                        serviceData.truncatedInstructions = _.truncate(
                            response.additionalInstructions, { length: 100 }
                        );
                    }
                    const serviceKind = _.get(response, 'serviceKind_EXT');
                    if (serviceKind !== 'salvage' && response.canDelay) {
                        serviceData.otherOperations.push('vendordelayed'); // Not a real SR Operation - but needed to reflect ability to delay date in CC
                    }
                    setSalvage(serviceKind === 'salvage');
                    if (serviceKind === 'salvage') {
                        const canCancelSalvage = _.get(response, 'canCancel_EXT');
                        if (canCancelSalvage && !serviceData.otherOperations.includes('specialistcanceled')) {
                            serviceData.otherOperations.push('specialistcanceled');
                        }
                    } else {
                        if (_.isUndefined(response.latestQuote)) {
                            const opns = serviceData.otherOperations.filter(item => item !== 'addinvoice');
                            serviceData.otherOperations = opns;
                        } else {
                            const quoteStatus = _.get(response.latestQuote, 'status');
                            if (quoteStatus !== 'approved') {
                                const opns = serviceData.otherOperations.filter(item => item !== 'addinvoice');
                                serviceData.otherOperations = opns;
                            }
                        }
                    }
                    setServiceDetails(serviceData);
                })
                .finally(() => {
                    setLoadingState(false);
                });
        }, [PRIMARY_PHONE_TYPE_MAP, authHeader, serviceRequestNumber]
    );

    const refreshPage = useCallback(() => {
        getServiceRequestDetails();
    }, [getServiceRequestDetails]);

    const handleCancelBid = useCallback(
        (actionData) => {
            setLoadingState(true);
            ServiceRequestService.cancelBid_EXT(actionData, authHeader)
                .then(getServiceRequestDetails);
        }, [authHeader, getServiceRequestDetails]
    );

    const handleAcceptWork = useCallback(
        (actionData) => {
            // ServiceRequestService.acceptWork(actionData, authHeader)
            setLoadingState(true);
            ServiceRequestService.acceptWork_EXT(actionData, authHeader)
                .then(getServiceRequestDetails);
        }, [authHeader, getServiceRequestDetails]
    );

    const handleCompleteWork = useCallback(
        () => {
            setLoadingState(true);
            ServiceRequestService.completeWork(serviceRequestNumber, authHeader)
                .then(getServiceRequestDetails);
        }, [authHeader, getServiceRequestDetails, serviceRequestNumber]
    );

    const handleDeclineWork = useCallback(
        (actionData) => {
            setLoadingState(true);
            ServiceRequestService.declineWork(actionData, authHeader)
                .then(getServiceRequestDetails);
        }, [authHeader, getServiceRequestDetails]
    );

    const handleDeclineToBid = useCallback(
        (actionData) => {
            setLoadingState(true);
            ServiceRequestService.declineToBid_EXT(actionData, authHeader)
                .then(getServiceRequestDetails);
        }, [authHeader, getServiceRequestDetails]
    );

    const handleCancelWork = useCallback(
        (actionData) => {
            setLoadingState(true);
            ServiceRequestService.cancelServiceRequest(actionData, authHeader)
                .then(getServiceRequestDetails);
        }, [authHeader, getServiceRequestDetails]
    );

    const handlePauseWork = useCallback(
        (actionData) => {
            setLoadingState(true);
            ServiceRequestService.reportBlocked(actionData, authHeader)
                .then(getServiceRequestDetails);
        }, [authHeader, getServiceRequestDetails]
    );

    const handleDelayWork = useCallback(
        (actionData) => {
            setLoadingState(true);
            ServiceRequestService.reportDelay(actionData, authHeader)
                .then(getServiceRequestDetails);
        }, [authHeader, getServiceRequestDetails]
    );

    const handleAddQuote = useCallback(
        (quoteVM) => {
            setLoadingState(true);
            ServiceRequestService.addOrReplaceQuote(
                serviceItems.publicID, quoteVM.value, authHeader
            ).then(getServiceRequestDetails);
        }, [authHeader, getServiceRequestDetails, serviceItems]
    );

    const handleAddInvoice = useCallback(
        (invoiceVM) => {
            setLoadingState(true);
            ServiceRequestService.addInvoice(
                serviceItems.publicID, invoiceVM.value, authHeader
            ).then(getServiceRequestDetails);
        }, [authHeader, getServiceRequestDetails, serviceItems]
    );

    const handleWithdrawInvoice = useCallback(
        (withdrawInvoiceVM) => {
            setLoadingState(true);
            ServiceRequestService.withdrawInvoice(
                withdrawInvoiceVM.value, authHeader
            ).then(getServiceRequestDetails);
        }, [authHeader, getServiceRequestDetails]
    );

    const handleResumeWork = useCallback(
        () => {
            setLoadingState(true);
            ServiceRequestService.resumeWork(serviceRequestNumber, authHeader)
                .then(getServiceRequestDetails);
        }, [authHeader, getServiceRequestDetails, serviceRequestNumber]
    );

    const checkIfActionButtonVisible = () => {
        let condition = !_.isUndefined(serviceDetails.firstOperation);
        if (isSalvage) {
            const nextStep = _.get(serviceItems, 'nextStep');
            const stepsArray = ['None', 'Pay Sasria Invoice', 'Waiting for Bid approval', 'Add Bid'];
            condition = condition && !_.get(serviceItems, 'vendorDeclined_EXT') && !_.get(serviceItems, 'agreedToBid_EXT')
                && !stepsArray.includes(nextStep);
        }
        return condition;
    };

    const performOperation = useCallback(
        (otherOperation) => {
            let actionProps = {
                serviceRequest: serviceItems,
                viewModelService: viewModelService
            };
            switch (otherOperation) {
                case 'specialistacceptedwork':
                    ModalNextProvider.showModal(
                        <AcceptWorkActionPopup {...actionProps} />
                    )
                        .then(handleAcceptWork)
                        .catch(_.noop);
                    break;
                case 'salVendorAgreedToBid_EXT':
                    ModalNextProvider.showModal(
                        <AcceptWorkActionPopup {...actionProps} />
                    )
                        .then(handleAcceptWork)
                        .catch(_.noop);
                    break;
                case 'specialistcompletedwork':
                    ModalNextProvider.showConfirm({
                        title: translator(messages.actionTitleCompleteWork),
                        message: translator(messages.actionMessageCompleteWork),
                        confirmButtonText: translator(messages.actionButtonCompleteWork),
                        cancelButtonText: translator(messages.actionButtonCancel)
                    })
                        .then(
                            (res) => {
                                if (res === 'confirm') {
                                    handleCompleteWork();
                                }
                            }
                        )
                        .catch(_.noop);
                    break;
                case 'specialistdeclined':
                    actionProps = {
                        ...actionProps,
                        modalTitle: isSalvage ? translator(messages.actionTitleDeclineToBid) : translator(messages.actionTitleDeclineWork),
                        modalMessage: isSalvage ? translator(messages.actionMessageDeclineToBid) : translator(messages.actionMessageDeclineWork),
                        defaultButtonLabel: isSalvage ? translator(messages.actionButtonDeclineToBid) : translator(messages.actionButtonDeclineWork),
                        cancelButtonLabel: translator(messages.actionButtonCancel)
                    };
                    ModalNextProvider.showModal(
                        <CommonWorkActionPopup {...actionProps} />
                    )
                        .then(isSalvage ? handleDeclineToBid : handleDeclineWork)
                        .catch(_.noop);
                    break;
                case 'salVendorDeclined_EXT':
                    actionProps = {
                        ...actionProps,
                        modalTitle: isSalvage ? translator(messages.actionTitleDeclineToBid) : translator(messages.actionTitleDeclineWork),
                        modalMessage: isSalvage ? translator(messages.actionMessageDeclineToBid) : translator(messages.actionMessageDeclineWork),
                        defaultButtonLabel: isSalvage ? translator(messages.actionButtonDeclineToBid) : translator(messages.actionButtonDeclineWork),
                        cancelButtonLabel: translator(messages.actionButtonCancel)
                    };
                    ModalNextProvider.showModal(
                        <CommonWorkActionPopup {...actionProps} />
                    )
                        .then(isSalvage ? handleDeclineToBid : handleDeclineWork)
                        .catch(_.noop);
                    break;
                case 'specialistcanceled':
                    actionProps = {
                        ...actionProps,
                        modalTitle: isSalvage ? translator(messages.actionTitleCancelBid) : translator(messages.actionTitleCancelWork),
                        modalMessage: isSalvage ? translator(messages.actionMessageCancelBid) : translator(messages.actionMessageCancelWork),
                        defaultButtonLabel: isSalvage ? translator(messages.actionButtonCancelBid) : translator(messages.actionButtonCancelWork),
                        cancelButtonLabel: translator(messages.actionButtonCancel)
                    };
                    ModalNextProvider.showModal(
                        <CommonWorkActionPopup {...actionProps} />
                    )
                        .then(isSalvage ? handleCancelBid : handleCancelWork)
                        .catch(_.noop);
                    break;
                case 'specialistwaiting':
                    actionProps = {
                        ...actionProps,
                        modalTitle: translator(messages.actionTitlePauseWork),
                        modalMessage: translator(messages.actionMessagePauseWork),
                        defaultButtonLabel: translator(messages.actionButtonPauseWork),
                        cancelButtonLabel: translator(messages.actionButtonCancel)
                    };
                    ModalNextProvider.showModal(
                        <CommonWorkActionPopup {...actionProps} />
                    )
                        .then(handlePauseWork)
                        .catch(_.noop);
                    break;
                case 'addquote':
                    ModalNextProvider.showModal(
                        <AddQuoteModal
                            requestKind={serviceItems.serviceKind_EXT}
                            vatCustomer={serviceItems.vatAppliedCustomer_EXT}
                            quoteValue={isSalvage ? {} : serviceItems.latestQuote}
                            serviceRequestID={serviceItems.publicID}
                            existingDocuments={serviceItems.documents}
                            isAddQuote={_.isNil(serviceItems.latestQuote)}
                            serviceRequestDocumentType={serviceItems.serviceQuoteDocumentType_EXT}
                        />
                    )
                        .then(handleAddQuote)
                        .catch(_.noop);
                    break;
                case 'salAddBid_EXT':
                    ModalNextProvider.showModal(
                        <AddQuoteModal
                            requestKind={serviceItems.serviceKind_EXT}
                            vatCustomer={serviceItems.vatAppliedCustomer_EXT}
                            quoteValue={serviceItems.latestQuote}
                            serviceRequestID={serviceItems.publicID}
                            existingDocuments={serviceItems.documents}
                            serviceRequestDocumentType={serviceItems.serviceQuoteDocumentType_EXT}
                        />
                    )
                        .then(handleAddQuote)
                        .catch(_.noop);
                    break;
                case 'addinvoice':
                    ModalNextProvider.showModal(
                        <AddInvoicePopup
                            requestKind={serviceItems.serviceKind_EXT}
                            vatCustomer={serviceItems.vatAppliedCustomer_EXT}
                            serviceRequestID={serviceItems.publicID}
                            existingDocuments={serviceItems.documents}
                            serviceRequestDocumentType={serviceItems.documentCheckList_Ext}
                            invoiceData={{}}
                        />
                    )
                        .then(handleAddInvoice)
                        .catch(_.noop);
                    break;
                case 'vendordelayed':
                    ModalNextProvider.showModal(
                        <DelayWorkActionPopup {...actionProps} />
                    )
                        .then(handleDelayWork)
                        .catch(_.noop);
                    break;
                case 'specialistresumedwork':
                    ModalNextProvider.showConfirm({
                        title: translator(messages.actionTitleResumeWork),
                        message: translator(messages.actionMessageResumeWork),
                        confirmButtonText: translator(messages.actionButtonResumeWork),
                        cancelButtonText: translator(messages.actionButtonCancel)
                    })
                        .then(
                            (res) => {
                                if (res === 'confirm') {
                                    handleResumeWork();
                                }
                            }
                        )
                        .catch(_.noop);
                    break;
                default:
                    break;
            }
        }, [serviceItems, viewModelService, handleAcceptWork, translator, isSalvage, handleDeclineToBid, handleDeclineWork, handleCancelBid, handleCancelWork, handlePauseWork, handleAddQuote, handleAddInvoice, handleDelayWork, handleCompleteWork, handleResumeWork]
    );

    const generateActionItemOverrides = useMemo(
        () => {
            const { otherOperations } = serviceDetails;
            const actionOverrides = otherOperations.map((otherOperation, index) => {
                return {
                    [`actionItem${index}`]: {
                        content: lookupActionNameMessageKey(otherOperation),
                        onClick: () => performOperation(otherOperation)
                    }
                };
            });
            return Object.assign({}, ...actionOverrides);
        }, [lookupActionNameMessageKey, performOperation, serviceDetails]
    );

    const mailToTrigger = useCallback((event, email) => {
        event.preventDefault();
        window.location = `mailto:${email}`;
    }, []);

    const renderEmailLink = useCallback((emailID) => {
        return (
            <Link className={styles.link} onClick={(e) => mailToTrigger(e, emailID)} href="/">
                {emailID}
            </Link>
        );
    }, [mailToTrigger]);

    const phoneTrigger = useCallback((event, phoneNumber) => {
        event.preventDefault();
        window.location = `tel:${phoneNumber}`;
    }, []);

    const getDateModified = useCallback((items, index, { path: property }) => {
        if (typeof items[property] === 'undefined') {
            return '';
        }
        return <FormattedDate value={new Date(items[property])} format="short" />;
    }, []);

    const renderPhoneLink = useCallback((phone) => {
        return (
            <Link className={styles.link} onClick={(e) => phoneTrigger(e, phone)} href="/">
                {phone}
            </Link>
        );
    }, [phoneTrigger]);

    const getCellPremium = useCallback((rowData, rowIndex) => {
        return (
            <CurrencyField
                id={`totalamount_${rowIndex}`}
                value={rowData.totalAmount}
                readOnly
            />
        );
    }, []);

    const addNewMessage = useCallback(() => {
        ModalNextProvider.showModal(
            <CreateNewMessagePopup
                serviceRequestNumber={serviceRequestNumber}
                authHeader={authHeader}
            />
        ).then(getServiceRequestDetails);
    }, [authHeader, getServiceRequestDetails, serviceRequestNumber]);

    const replyMessage = useCallback((messageData) => {
        ModalNextProvider.showModal(
            <ReplyMessagePopup
                serviceRequestNumber={serviceRequestNumber}
                serviceItems={serviceItems}
                messageData={messageData}
            />
        ).then(getServiceRequestDetails);
    }, [getServiceRequestDetails, serviceItems, serviceRequestNumber]);

    const getInvoiceStatus = useCallback((rowData) => {
        return translator({
            id: `typekey.ServiceRequestInvoiceStatus.${rowData.status}`,
            defaultMessage: rowData.status
        });
    }, [translator]);

    const getTypeValue = useCallback((rowData) => {
        return translator({
            id: `typekey.ServiceRequestMessageType.${rowData.type}`,
            defaultMessage: rowData.status
        });
    }, [translator]);

    const withdrawInvoice = useCallback((invoiceData) => {
        ModalNextProvider.showModal(
            <WithdrawInvoicePopup invoiceID={invoiceData.publicID} />
        ).then(handleWithdrawInvoice);
    }, [handleWithdrawInvoice]);

    const getDocumentDownloadLink = useCallback(
        (item) => {
            // eslint-disable-next-line camelcase
            const { publicID, sessionID, availableInSharepoint_EXT } = item;
            const linkHref = ServiceRequestService.downloadDocumentLink(publicID, sessionID);
            return (
                // eslint-disable-next-line camelcase
                (availableInSharepoint_EXT) ? (
                    <Link className={styles.link} href={linkHref}>
                        {item.name}
                    </Link>
                ) : (
                    `${item.name}(Currently not available - Please try later)`
                )
            );
        },
        []
    );

    const addNewInvoice = useCallback(() => {
        ModalNextProvider.showModal(
            <AddInvoicePopup
                requestKind={serviceItems.serviceKind_EXT}
                vatCustomer={serviceItems.vatAppliedCustomer_EXT}
                serviceRequestID={serviceItems.publicID}
                existingDocuments={serviceItems.documents}
                serviceRequestDocumentType={serviceItems.documentCheckList_Ext}
                invoiceData={{}}
            />
        ).then(handleAddInvoice);
    }, [handleAddInvoice, serviceItems]);

    const getWithdrawButton = useCallback((invoiceData) => {
        if (!invoiceData.canWithdraw) {
            return null;
        }

        return (
            <Button
                id="withdrawButton"
                type="secondary"
                icon="mi-delete"
                onClick={() => withdrawInvoice(invoiceData)}
            />
        );
    }, [withdrawInvoice]);

    useEffect(() => {
        getServiceRequestDetails();
        // disable re render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }
    const actionDropDownVisibility = () => {
        if ((_.get(serviceItems, 'serviceKind_EXT') !== 'salvage' && serviceDetails.otherOperations.length > 0)
            || (_.get(serviceItems, 'serviceKind_EXT') === 'salvage' && !_.get(serviceItems, 'vendorDeclined_EXT') && _.get(serviceItems, 'nextStep') !== 'None' && _.get(serviceItems, 'nextStep') !== 'Pay Sasria Invoice')) {
            return true;
        }
        return false;
    };

    const addInvoiceVisibility = () => {
        let isAddInvoiceVisible = false;
        if (!_.get(serviceItems, 'vendorDeclined_EXT') && _.get(serviceItems, 'canAddInvoice') && !_.isUndefined(serviceItems.latestQuote)) {
            const quoteStatus = _.get(serviceItems.latestQuote, 'status');
            if (quoteStatus === 'approved') {
                isAddInvoiceVisible = true;
            }
        }
        if (_.get(serviceItems, 'serviceKind_EXT') === 'salvage') {
            isAddInvoiceVisible = false;
        }
        return isAddInvoiceVisible;
    };

    const deriveCustomerContactName = () => {

        let fullCustomerName = '';
        const name = _.get(serviceItems, 'customerContact.displayName');
        fullCustomerName = name;
        const emailName = _.get(serviceItems, 'customerContact.emailAddress1');
        const cellPhone = _.get(serviceItems, 'customerContact.cellNumber');
        const workPhone = _.get(serviceItems, 'customerContact.workNumber');
        const homePhone = _.get(serviceItems, 'customerContact.homeNumber');
        if (!_.isEmpty(emailName)) {
            fullCustomerName = `${fullCustomerName},${emailName}`;
        }
        if (!_.isEmpty(cellPhone)) {
            fullCustomerName = `${fullCustomerName},${cellPhone}`;
        }
        else if (!_.isEmpty(workPhone)) {
            fullCustomerName = `${fullCustomerName},${workPhone}`;
        }
        else if (!_.isEmpty(homePhone)) {
            fullCustomerName = `${fullCustomerName},${homePhone}`;
        }

        return fullCustomerName;
    };

    const deriveCustomerPhoneNumber = () => {

        let phno = '';
        const cellPhone = _.get(serviceItems, 'customerContact.cellNumber');
        const workPhone = _.get(serviceItems, 'customerContact.workNumber');
        const homePhone = _.get(serviceItems, 'customerContact.homeNumber');
        if (!_.isEmpty(cellPhone)) {
            phno = cellPhone;
        }
        else if (!_.isEmpty(workPhone)) {
            phno = workPhone;
        }
        else if (!_.isEmpty(homePhone)) {
            phno = homePhone;
        }

        return phno;
    };

    const override = {
        '@field': {
            labelPosition: 'left'
        },

        // customerContactName: {
        //     value: deriveCustomerContactName()
        // },

        customerContactPhone: {
            value: deriveCustomerPhoneNumber(),
            visible: !_.isEmpty(_.get(serviceItems, 'customerContact.cellNumber')) || !_.isEmpty(_.get(serviceItems, 'customerContact.workNumber')) || !_.isEmpty(_.get(serviceItems, 'customerContact.homeNumber'))
        },

        customerContactEmail: {
            visible: !_.isEmpty(_.get(serviceItems, 'customerContact.emailAddress1'))
        },

        bidStatusHeading: {
            content: bidAcceptedStatus === true ? 'Bid Accepted' : 'Bid Awarded to Another Vendor',
            visible: _.get(serviceItems, 'serviceKind_EXT') === 'salvage' && (_.get(serviceItems, '_SalvageQuoteStatus_EXT') === 'declined_EXT' || _.get(serviceItems, '_SalvageQuoteStatus_EXT') === 'approved')
        },
        serviceRequestsDetailsTitle: {
            content: `${_.get(serviceItems, 'insuredContact_EXT.displayName')} - ${_.get(serviceItems, 'claimID')}`
        },
        // customerContactEmail: {
        //     content: renderEmailLink(_.get(serviceItems, 'customerContact.emailAddress1'))
        // },
        claimHandlerEmail: {
            content: renderEmailLink(_.get(serviceItems, 'claimHandler.emailAddress1'))
        },
        claimHandlerContact: {
            content: renderPhoneLink(_.get(serviceItems, 'claimHandler.cellNumber'))
        },
        customerContactNumber: {
            content: renderPhoneLink(_.get(serviceItems, 'customerContact.homeNumber'))
        },
        actionsDropdown: {
            visible: actionDropDownVisibility()
        },
        addInvoiceButtonContainer: {
            visible: addInvoiceVisibility()
        },
        actionDefaultButton: {
            content: lookupActionNameMessageKey(serviceDetails.firstOperation),
            onClick: () => performOperation(serviceDetails.firstOperation),
            visible: checkIfActionButtonVisible()
        },
        addNewMessageButton: {
            visible: !_.get(serviceItems, 'vendorDeclined_EXT')
        },
        ...generateActionItemOverrides,
        messageTable: {
            data: _.get(serviceItems, 'messages'),
            onRowClick: replyMessage
        },
        invoiceTable: {
            data: _.get(serviceItems, 'invoices')
        },
        historyTable: {
            data: _.get(serviceItems, 'history')
        },
        documentTable: {
            data: _.get(serviceItems, 'documents')
        },
        invoiceTotalAmountColumn: {
            onCell: getCellPremium
        },
        invoiceStatusColumn: {
            onCell: getInvoiceStatus
        },
        typeColumn: {
            onCell: getTypeValue
        },
        documentName: {
            onCell: getDocumentDownloadLink
        },
        refreshButton: {
            onClick: refreshPage
        }
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveValue: readValue,
        resolveCallbackMap: {
            getDateModified: getDateModified,
            addNewInvoice: addNewInvoice,
            addNewMessage: addNewMessage,
            getWithdrawButton: getWithdrawButton
        }
    };

    return renderContentFromMetadata(metadata.pageContent, override, resolvers);
}

ServiceRequestDetailsPage.propTypes = {
    match: PropTypes.shape({
        params: PropTypes.shape({
            serviceRequestNumber: PropTypes.string
        })
    }).isRequired
};

export default withRouter(ServiceRequestDetailsPage);
